import { forwardRef, useEffect, useState } from 'react'
import { cn } from '@/lib/cn'

const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/

const baseClasses =
  'box-border h-11 w-full rounded-xl border border-gray-200 bg-white/95 px-3.5 text-sm tabular-nums text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-150 placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:placeholder:text-gray-500'

export interface TimeInputProps {
  label?: string
  error?: string
  /** Current value in HH:mm 24-hour format, or empty string. */
  value?: string
  /** Fired with the normalized HH:mm value (or empty string). */
  onChange?: (value: string) => void
  onBlur?: () => void
  name?: string
  disabled?: boolean
  required?: boolean
  className?: string
  id?: string
  placeholder?: string
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Parses various input shapes into a normalized HH:mm (24h) string.
 * Returns '' for empty input, null for unparseable.
 */
function parseToHHmm(input: string): string | null {
  const trimmed = input.trim()
  if (trimmed === '') return ''
  if (HH_MM.test(trimmed)) return trimmed
  const digits = trimmed.replace(/[^\d]/g, '')
  if (/^\d{1,2}$/.test(digits)) {
    const h = Number(digits)
    if (h >= 0 && h <= 23) return `${pad(h)}:00`
  }
  if (/^\d{3}$/.test(digits)) {
    const h = Number(digits[0])
    const m = Number(digits.slice(1))
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) return `${pad(h)}:${pad(m)}`
  }
  if (/^\d{4}$/.test(digits)) {
    const h = Number(digits.slice(0, 2))
    const m = Number(digits.slice(2))
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) return `${pad(h)}:${pad(m)}`
  }
  return null
}

function stepTime(hhmm: string, deltaMinutes: number): string {
  const m = HH_MM.exec(hhmm)
  let total = m ? Number(m[1]) * 60 + Number(m[2]) : 0
  total = ((total + deltaMinutes) % 1440 + 1440) % 1440
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`
}

/**
 * 24-hour time input with free-text typing, auto-formatting, and arrow-key stepping.
 * - Type "2300" or "23:00" → stored as "23:00"
 * - ↑/↓ steps minutes (or hours when caret is on the HH side)
 * - Shift+↑/↓ steps by 15 minutes / 5 hours
 */
export const TimeInput = forwardRef<HTMLInputElement, TimeInputProps>(
  function TimeInput(
    {
      label,
      error,
      value = '',
      onChange,
      onBlur,
      name,
      disabled,
      required,
      className,
      id: idProp,
      placeholder = 'HH:mm',
    },
    ref,
  ) {
    const id = idProp ?? name
    const [display, setDisplay] = useState<string>(value)

    useEffect(() => {
      setDisplay(value)
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      let next = e.target.value.replace(/[^\d:]/g, '').slice(0, 5)
      // Auto-insert colon after typing 3+ digits with no colon yet (e.g. "230" → "23:0").
      if (next.length >= 3 && !next.includes(':') && /^\d{3,}$/.test(next)) {
        next = next.slice(0, 2) + ':' + next.slice(2)
      }
      setDisplay(next)
      if (HH_MM.test(next)) onChange?.(next)
      else if (next === '') onChange?.('')
    }

    const handleBlur = (): void => {
      const parsed = parseToHHmm(display)
      if (parsed != null) {
        if (parsed !== display) setDisplay(parsed)
        if (parsed !== value) onChange?.(parsed)
      } else {
        // Invalid input — revert to last valid value
        setDisplay(value)
      }
      onBlur?.()
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
      e.preventDefault()
      const dir = e.key === 'ArrowUp' ? 1 : -1
      const input = e.currentTarget
      const cursorAt = input.selectionStart ?? display.length
      // Caret in HH segment (positions 0-2 = before/on the colon) steps hours.
      // Caret in mm segment (positions 3+) steps minutes.
      const inMinutes = cursorAt > 2
      const stepMagnitude = e.shiftKey
        ? inMinutes
          ? 15
          : 300
        : inMinutes
          ? 1
          : 60
      const base = HH_MM.test(display)
        ? display
        : parseToHHmm(display) ?? '00:00'
      const next = stepTime(base, dir * stepMagnitude)
      setDisplay(next)
      onChange?.(next)
      requestAnimationFrame(() => {
        if (inMinutes) input.setSelectionRange(3, 5)
        else input.setSelectionRange(0, 2)
      })
    }

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={id}
            className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400"
          >
            {label}
            {required && <span className="text-danger"> *</span>}
          </label>
        )}
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          id={id}
          name={name}
          disabled={disabled}
          placeholder={placeholder}
          value={display}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onFocus={(e) => e.currentTarget.select()}
          aria-invalid={!!error}
          aria-describedby={error && id ? `${id}-error` : undefined}
          aria-required={required ?? undefined}
          className={cn(
            baseClasses,
            error ? 'border-danger focus:border-danger/70 focus:ring-danger/10' : null,
            className,
          )}
        />
        {error && (
          <p id={id ? `${id}-error` : undefined} className="mt-1 text-sm text-danger">
            {error}
          </p>
        )}
      </div>
    )
  },
)

TimeInput.displayName = 'TimeInput'
